<template>
  <div class="w-full mt-3">
    <div class="flex items-center">
      <div class="won-color-green font-semibold uppercase">Card Details</div>
      <lock class="ml-2.5" />
    </div>

    <div
      class="w-full pt-2.5 px-6 pb-4 rounded-lg mt-3 payment-form"
      style="background-color: #f5f5f5; position: relative"
    >
      <wrapper label="Card Number">
        <div id="payment-card-number" />
        <credit-card class="card-input-icon" />
      </wrapper>

      <div class="grid" style="grid-template-columns: 1fr 1fr; gap: 0 14px">
        <wrapper>
          <div id="payment-card-date" />
        </wrapper>

        <wrapper>
          <div id="payment-card-cvc" />
          <info class="card-input-icon mb-0.5" />
        </wrapper>
      </div>

      <div
        v-if="!!error"
        class="text-xs won-color-error absolute font-medium"
        style="bottom: -18px; left: 0"
      >
        {{ error }}
      </div>
    </div>

    <won-button class="w-full mt-6" @click="handleSubmit">
      {{ loading ? 'Processing...' : 'Submit Payment' }}

      <template v-if="loading" #icon>
        <loading class="animate-spin" />
      </template>
    </won-button>

    <div class="w-full flex justify-center items-center mt-2.5">
      <lock class="mr-1" />
      <div
        class="text-xs won-color-subtitle-gray"
        style="letter-spacing: 0.15px"
      >
        All Payment are PCI compliant &amp; SSL Secure
      </div>
    </div>

    <div class="mt-5 flex justify-center w-full">
      <stripe-icon />
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

import Text from '../../forms/elements/text.vue'
import Wrapper from './form-item-wrapper.vue'
import WonButton from '../won-button.vue'

import Lock from '../../icons/lock.vue'
import Info from '../../icons/info.vue'
import Loading from '../../icons/loading.vue'
import CreditCard from '../../icons/credit-card.vue'
import StripeIcon from '../../icons/stripe.vue'

import { useTokens } from '../../store/token.store'
import { getPurchaseSecret } from '../../api/purchase'
import { storeToRefs } from 'pinia'

export default {
  components: {
    Lock,
    Text,
    Wrapper,
    CreditCard,
    Info,
    WonButton,
    Loading,
    StripeIcon,
  },

  emits: ['payment-form:success'],

  setup() {
    const loading = ref(false)
    const error = ref(null)
    const stripe = ref(null)
    const tokenStore = useTokens()

    const { selectedOption } = storeToRefs(tokenStore)

    return {
      loading,
      error,
      stripe,
      selectedOption,
      stripeKey: tokenStore.stripeKey,
    }
  },

  data: () => ({
    cardNumber: null,
    cardExpire: null,
    cardCvc: null,

    elementsOptions: {
      classes: {
        base: 'payment-input',
      },
    },
  }),

  mounted() {
    this.stripe = Stripe(this.stripeKey)
    this.creteElements()
    this.addListeners()
  },

  methods: {
    creteElements() {
      const elements = this.stripe.elements({
        appearance: {
          theme: 'none',
        },
      })

      this.cardNumber = elements.create('cardNumber', {
        ...this.elementsOptions,
        placeholder: 'Card Number',
      })
      this.cardNumber.mount('#payment-card-number')

      this.cardExpire = elements.create('cardExpiry', {
        ...this.elementsOptions,
      })
      this.cardExpire.mount('#payment-card-date')

      this.cardCvc = elements.create('cardCvc', {
        ...this.elementsOptions,
        placeholder: 'Security Code',
      })
      this.cardCvc.mount('#payment-card-cvc')
    },

    addListeners() {
      const elements = [this.cardNumber, this.cardExpire, this.cardCvc]

      for (const element of elements) {
        element.on('change', this.handleStripeError)
      }
    },

    handleStripeError(event) {
      const message = event.error ? event.error.message : ''
      this.error = message
    },

    async handleSubmit() {
      this.loading = true

      try {
        const tokenResult = await getPurchaseSecret(this.selectedOption.id)

        const result = await this.stripe.confirmCardPayment(tokenResult, {
          payment_method: { card: this.cardNumber },
        })

        if (result?.error) {
          this.error = result?.error?.message || 'Something went wrong'
          return
        }

        if (result.paymentIntent.status === 'succeeded') {
          this.$emit('payment-form:success')
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.card-input-icon {
  position: absolute;
  right: 8px;
  bottom: 10px;
}
</style>
